import React, { useEffect, useState, type FC, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader } from "shared/components/Loader";
import { AxiosInstance } from "shared/utils/axios";
import {
  clearPendingProductEntry,
  getDeckProductContext,
  getPendingProductEntry,
  hasIncomingProductContext,
  MY_CROPWISE_ROOT,
  resolveAccessibleAssessmentPath,
  type WorkspaceOrganization,
} from "shared/utils/productEntry";

const loadOrganizations = async (
  workspaceId: string,
  signal: AbortSignal
): Promise<WorkspaceOrganization[] | null> => {
  const response = await AxiosInstance.get<WorkspaceOrganization[]>(
    "/api/orgs",
    {
      params: { workspaceId },
      signal,
    }
  );
  return Array.isArray(response.data) ? response.data : null;
};

// Incoming Deck of Apps links land with ?workspace=&organization=. If that
// workspace can open Carbon Engine, go straight to assessments. Otherwise
// send the user to My Cropwise instead of a broken portal page.
export const ProductEntryRedirect: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingEntry] = useState(() =>
    new URLSearchParams(location.search).has("code")
      ? getPendingProductEntry()
      : undefined
  );
  const fromDeck = hasIncomingProductContext(
    location.pathname,
    location.search
  );
  const restored = new URLSearchParams(location.search).has("code")
    ? pendingEntry
    : undefined;
  const incoming = fromDeck || restored !== undefined;
  const entrySearch = fromDeck ? location.search : restored?.search ?? "";
  const entryHash = fromDeck ? location.hash : restored?.hash ?? "";

  useEffect(() => {
    if (!incoming) {
      return;
    }
    const controller = new AbortController();
    let active = true;
    const resolveEntry = async (): Promise<void> => {
      const context = getDeckProductContext(entrySearch);
      try {
        const organizations = context.workspaceId
          ? await loadOrganizations(context.workspaceId, controller.signal)
          : null;
        if (!active) {
          return;
        }
        clearPendingProductEntry();
        navigate(
          `${resolveAccessibleAssessmentPath(
            context,
            organizations
          )}${entryHash}`,
          { replace: true }
        );
      } catch (error) {
        if (!active) {
          return;
        }
        console.error("Failed to resolve incoming product workspace:", error);
        clearPendingProductEntry();
        navigate(MY_CROPWISE_ROOT, { replace: true });
      }
    };
    void resolveEntry();
    return () => {
      active = false;
      controller.abort();
    };
  }, [incoming, entryHash, entrySearch, navigate]);

  if (incoming) {
    return <Loader mask={true} message="Loading..." />;
  }
  return <>{children}</>;
};
